import { Fingerprint, KeyRound, ShieldCheck } from 'lucide-react'
import * as React from 'react'
import { cn } from '../../lib/cn'
import { Button } from '../atoms/Button'

/**
 * WebAuthnEnrollStep — the screen the user sees when the pipeline runtime
 * pauses at `credential_set` with `factor: "webauthn_platform"` or
 * `"webauthn_security_key"` during enrollment.
 *
 * The component is intentionally **headless of the WebAuthn API**. The
 * actual `navigator.credentials.create` call lives in the parent (which
 * already has `@simplewebauthn/browser` installed in dp-auth-ui). This
 * keeps the DS dep-light and lets the parent handle the per-flow
 * concerns (which library to use, how to encode the attestation for
 * transport, where to surface NotAllowedError vs other failures).
 *
 * What the molecule owns:
 *  - the icon + heading + helper copy that frame the ceremony
 *  - a "Start" button that triggers `onCreate()`
 *  - the visible state transitions (idle → running → success → error)
 *  - retry affordance when the ceremony fails
 *  - a11y semantics on the live status region
 *
 * What the parent owns:
 *  - the WebAuthn API call (and the challenge it sends in)
 *  - transport encoding of the attestation
 *  - mapping `NotAllowedError` / `InvalidStateError` to user copy
 *  - whether the ceremony is for a platform authenticator or a roaming
 *    security key (affects icon + helper text props)
 */

/**
 * Ceremony status — the molecule's state machine.
 *
 *   idle    → user hasn't started yet (initial)
 *   running → onCreate() is in flight; "use your authenticator now"
 *   success → onCreate() resolved successfully; success copy + green
 *             icon shown until the parent navigates away
 *   error   → onCreate() rejected; error copy + retry button shown
 */
export type WebAuthnEnrollStatus = 'idle' | 'running' | 'success' | 'error'

export interface WebAuthnEnrollStepProps {
  /**
   * The kind of authenticator the parent is asking the user to register.
   * Drives icon + helper copy. Both kinds use the same DOM structure;
   * only the copy + glyph differ.
   *
   *   "platform"     → built-in (Face ID, Windows Hello, Touch ID)
   *   "security-key" → roaming (YubiKey, FIDO2 USB / NFC token)
   */
  kind?: 'platform' | 'security-key'
  /**
   * Triggered when the user clicks Start (or Retry after an error).
   * The parent runs the WebAuthn ceremony and returns the attestation;
   * the molecule only awaits + flips its visible state.
   */
  onCreate: () => Promise<void>
  /**
   * Called when the user clicks the "Cancel enrollment" button. Optional —
   * when absent, no cancel is rendered.
   */
  onCancel?: () => void
  /**
   * Called when the user clicks the "Skip for now" tertiary button. Renders
   * only when the parent passes onSkip (driven by the pause envelope's
   * can_skip field — set by the BE when credential_set was configured with
   * `optional: true`). Parent resumes the pipeline with `{ action: "skip" }`.
   */
  onSkip?: () => void | Promise<void>
  /** Override the skip button label. Default: "Skip for now". */
  skipLabel?: string
  /**
   * Error message surfaced when the ceremony fails. The parent maps the
   * raw `DOMException` (NotAllowedError, InvalidStateError, etc.) into
   * user copy before passing it here. Presence of `error` flips the
   * visible status to "error".
   */
  error?: string
  /**
   * Override the visible heading. Default per `kind`:
   *   platform     → "Use your device's built-in authenticator"
   *   security-key → "Use your security key"
   */
  heading?: string
  /** Optional helper text rendered under the heading. */
  helperText?: string
  /** Extra classes for the wrapper. */
  className?: string
}

const HEADING_DEFAULTS: Record<NonNullable<WebAuthnEnrollStepProps['kind']>, string> = {
  platform: "Use your device's built-in authenticator",
  'security-key': 'Use your security key',
}

const HELPER_DEFAULTS: Record<NonNullable<WebAuthnEnrollStepProps['kind']>, string> = {
  platform:
    'You can sign in faster next time with Face ID, Touch ID, or Windows Hello — whatever your device already supports.',
  'security-key':
    'Insert your security key now. You may be asked to touch it to confirm. We never see your private key.',
}

export const WebAuthnEnrollStep = React.forwardRef<HTMLDivElement, WebAuthnEnrollStepProps>(
  (
    {
      kind = 'platform',
      onCreate,
      onCancel,
      onSkip,
      skipLabel = 'Skip for now',
      error,
      heading,
      helperText,
      className,
    },
    ref,
  ) => {
    const [status, setStatus] = React.useState<WebAuthnEnrollStatus>('idle')

    // Reflect parent-supplied error into the visible status. We don't
    // own `error` so we don't clear it — the parent does on retry.
    React.useEffect(() => {
      if (error) setStatus('error')
    }, [error])

    const handleStart = React.useCallback(async () => {
      if (status === 'running') return
      setStatus('running')
      try {
        await onCreate()
        setStatus('success')
      } catch {
        // The parent surfaces the error via the `error` prop; we only
        // flip the visible status here. Re-throwing would leak the raw
        // DOMException into the React event handler chain.
        setStatus('error')
      }
    }, [onCreate, status])

    const resolvedHeading = heading ?? HEADING_DEFAULTS[kind]
    const resolvedHelper = helperText ?? HELPER_DEFAULTS[kind]

    const Icon = kind === 'platform' ? Fingerprint : KeyRound
    const buttonLabel =
      status === 'running'
        ? 'Waiting for authenticator…'
        : status === 'error'
          ? 'Try again'
          : status === 'success'
            ? 'Enrolled'
            : kind === 'platform'
              ? 'Set up biometrics'
              : 'Register security key'

    return (
      <div ref={ref} className={cn('flex flex-col items-center gap-5 text-center', className)}>
        <div className="flex flex-col items-center gap-1">
          <h2 className="text-heading-sm font-semibold text-ia-text">{resolvedHeading}</h2>
          {resolvedHelper ? (
            <p className="text-body-sm text-ia-text-secondary">{resolvedHelper}</p>
          ) : null}
        </div>

        {/* Status glyph. Color flips on success (green) and error (red);
            idle and running share the neutral primary tone so the user's
            attention stays on the ceremony copy + button below. */}
        <div
          className={cn(
            'flex h-20 w-20 items-center justify-center rounded-full border',
            status === 'success'
              ? 'border-ia-success bg-ia-success/10 text-ia-success'
              : status === 'error'
                ? 'border-ia-error bg-ia-error/10 text-ia-error'
                : 'border-ia-border bg-surface text-primary',
          )}
          aria-hidden
        >
          {status === 'success' ? (
            <ShieldCheck className="h-10 w-10" />
          ) : (
            <Icon className="h-10 w-10" />
          )}
        </div>

        {/* Live region: announces status changes for AT users. Kept
            mounted (empty when idle) so SR cadence stays deterministic
            across status transitions. */}
        <div
          role="status"
          aria-live="polite"
          className={cn(
            'min-h-5 text-body-sm',
            status === 'success'
              ? 'text-ia-success'
              : status === 'error'
                ? 'text-ia-error'
                : 'text-ia-text-secondary',
          )}
        >
          {status === 'running' && 'Waiting for your authenticator…'}
          {status === 'success' && 'Authenticator enrolled.'}
          {status === 'error' && (error || 'Something went wrong. Try again.')}
          {status === 'idle' && ' '}
        </div>

        <div className="flex w-full flex-col gap-2">
          <Button
            type="button"
            variant="primary"
            size="lg"
            onClick={handleStart}
            disabled={status === 'running' || status === 'success'}
            loading={status === 'running'}
            className="w-full"
          >
            {buttonLabel}
          </Button>
          {onCancel ? (
            <Button
              type="button"
              variant="secondary"
              size="lg"
              onClick={onCancel}
              disabled={status === 'running'}
              className="w-full"
            >
              Cancel enrollment
            </Button>
          ) : null}
          {onSkip ? (
            // Tertiary skip — mirrors TotpEnrollStep + FactorPickerStep.
            // Rendered only when parent passes onSkip (env.can_skip true
            // on the pause envelope; admin set credential_set.optional=true).
            <button
              type="button"
              disabled={status === 'running'}
              onClick={() => {
                void onSkip()
              }}
              className={cn(
                'mx-auto rounded-md px-3 py-1.5 text-body-sm font-medium text-ia-text-secondary',
                'transition-colors duration-150 hover:text-ia-text',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
                'disabled:cursor-not-allowed disabled:opacity-50',
              )}
            >
              {skipLabel}
            </button>
          ) : null}
        </div>
      </div>
    )
  },
)
WebAuthnEnrollStep.displayName = 'WebAuthnEnrollStep'
